import { m, useReducedMotion } from 'framer-motion';
import type React from 'react';

interface RevealProps {
  children: React.ReactNode;
  /** Stagger delay in ms. Converts to seconds for Framer Motion. */
  delay?: number;
  className?: string;
  /**
   * HTML element to render as. Defaults to 'div'.
   * Use 'li' when Reveal wraps a list item so the DOM stays semantically valid
   * (an ol/ul must not have a div child before its li children).
   */
  as?: 'div' | 'li' | 'section' | 'article';
}

/**
 * Scroll-triggered reveal wrapper.
 * Fades + y-translates (y: 24 → 0) when entering the viewport once.
 * Respects prefers-reduced-motion: skips y-translate, keeps subtle opacity.
 *
 * Uses Framer Motion's `m` component (requires a LazyMotion provider ancestor).
 */
function Reveal({ children, delay = 0, className, as = 'div' }: RevealProps) {
  const prefersReducedMotion = useReducedMotion();

  const variants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 24,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  // m[element] is the lightweight Framer Motion API for LazyMotion contexts
  const MotionElement = m[as];

  return (
    <MotionElement
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 0.6,
        delay: delay / 1000,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      variants={variants}
    >
      {children}
    </MotionElement>
  );
}

export { Reveal };
export type { RevealProps };
export default Reveal;
